import React from "react";

/**
 * QrCode — draws the QR geometry the server computed.
 *
 * ⚠️ THE SERVER SENDS A PATH, NOT AN IMAGE, AND NOT AN `<svg>`. Three reasons,
 * and the first is the one that made the decision:
 *
 *  - the `qrcode` library's own SVG output hardcodes `fill="#ffffff"` and
 *    `fill="#000000"`. That is a colour literal arriving from the server into
 *    markup the palette cannot reach, so the code would stay black-on-white in
 *    dark mode and under the print swap. Drawing the path with `currentColor`
 *    means it inherits the card's ink like any other glyph;
 *  - no QR library on the client, so nothing is added to a bundle already
 *    flagged as too large;
 *  - a path of run-length subpaths is a fraction of the size of a data-URI PNG,
 *    and stays crisp at any print resolution.
 *
 * ⚠️ `shapeRendering="crispEdges"` is not optional. Anti-aliased module edges
 * are what makes a small printed QR fail to scan — the reader sees grey where
 * it needs black or white.
 *
 * The quiet zone is part of the viewBox rather than baked into the path: the
 * spec asks for four modules of clear margin on every side, and a QR printed
 * hard against a card border is the other common reason a code will not scan.
 * It is left TRANSPARENT so it takes the card's own surface — which is the
 * light colour in both themes and under the print swap. Painting it a fixed
 * colour is how a code ends up dark-on-dark for a dark-mode viewer.
 *
 * ⚠️ Always give it an explicit size. An `<svg>` with only a viewBox collapses
 * to its default 300×150 in some layouts and to nothing in a flex column.
 */
const QrCode = ({ size, path, quietZone = 4, className = "", style, title = "QR code" }) => {
  if (!size || !path) return null;
  const box = size + quietZone * 2;

  return (
    <svg
      viewBox={`0 0 ${box} ${box}`}
      className={className}
      style={style}
      shapeRendering="crispEdges"
      role="img"
      aria-label={title}
    >
      <g transform={`translate(${quietZone} ${quietZone})`}>
        <path d={path} fill="currentColor" />
      </g>
    </svg>
  );
};

export default QrCode;
